import { useCallback } from "react";
import { sha256 } from "js-sha256";
import { EVENTS_URL, SEND_UNAUTHORIZED_EVENT_PATH } from "../config";
import { RequestConfigType } from "./types/request-config";
import useFetch from "../use-fetch";
import { fetchWithKeepaliveFallback, reserveKeepalive } from "../keepalive-budget";
import { KEEPALIVE_SENDER, trackKeepaliveRefusal } from "../keepalive-refusal";
import { ERROR_BEACON_EVENT_NAME } from "../web-event-beacon";
import { isBatchedWebEventName, queueWebEvent, type WebEventBatchFailure } from "../web-event-batch";
import wrapperFetchJsonResponse from "../wrapper-fetch-json-response";
import HTTP_CODES_ENUM from "../types/http-codes";
import type { FetchJsonResponse } from "../types/fetch-json-response";
import {
    BiqQueryPropertyType,
    FacebookPropertyType,
    TrackingiqQueryPEventFields,
} from "./types/EventsEnum";
import {
    classifyFetchFailure,
    type FetchFailureReason,
    isBenignFetchFailure,
    logFetchCancellation,
    settleFetchFailureReason,
} from "../fetch-failure";
import useAuthTokens from "~/services/auth/use-auth-tokens";
import useUserId from "~/hooks/useUserId";
import { useLogEvent, type LogEvent } from "~/hooks/useLogEvent";
import { describeError } from "~/utils/errorTelemetry";

export const EVENT_SEND_LOG_EVENT = {
    TAG: "api-event-send-failed",
    GUEST_SEND_FAILED: "A guest event could not be sent",
    BATCH_SEND_FAILED: "An event batch could not be sent",
    BATCHED_SEND_FAILED: "A batched event could not be sent on its own",
} as const;

type EventPropertyValue = string | number | boolean | null | undefined;

type WebEventProperties = Partial<Record<TrackingiqQueryPEventFields, EventPropertyValue>>;

type FacebookEventProperties = {
    [K in TrackingiqQueryPEventFields]?: K extends TrackingiqQueryPEventFields.UserData
        ? { external_id?: string; [key: string]: unknown }
        : EventPropertyValue;
};

export type EventRequest =
    | {
          type: BiqQueryPropertyType.WEB_EVENT;
          properties: WebEventProperties;
      }
    | {
          type: FacebookPropertyType.FACEBOOK_EVENT;
          properties: FacebookEventProperties;
      };

export type EventResponse = void;

// What a queued event resolves to: the batch route answers 204, and nothing
// waits for the batch. A new object each time, so no caller sees another's edits.
const queuedEventResponse = (): FetchJsonResponse<EventResponse> => ({
    status: HTTP_CODES_ENUM.NO_CONTENT,
    data: undefined,
});

type GuestSendContext = {
    url: string;
    eventType: EventRequest["type"];
    eventName: EventPropertyValue;
    /** Whether the send asked for keepalive. */
    keepalive: boolean;
    /** Whether it was refused and sent again without keepalive. */
    wasRefused: boolean;
    onSettled: (failureReason: FetchFailureReason) => void;
    userId: string | null | undefined;
};

type SendFailureContext = {
    url: string;
    userId: string | null | undefined;
    message: string;
    data: Record<string, unknown>;
    /** Whether the send asked for keepalive, even if it went again without. */
    keepalive: boolean;
    /** Whether it was refused and sent again without keepalive. */
    wasRefused: boolean;
    /** Called with the final reason, once known. */
    onSettled?: (failureReason: FetchFailureReason) => void;
};

/**
 * Logs an event send that failed with an error, which nothing else reports.
 * Benign failures go under the same cancellation tag as `fetchBase` uses, so
 * the failed-send tag lists real network errors only.
 */
function logSendFailure(
    logEvent: LogEvent,
    error: unknown,
    { url, userId, message, data, keepalive, wasRefused, onSettled }: SendFailureContext
): void {
    const failureReason = classifyFetchFailure(error, { keepalive, wasRefused });
    const logCancellation = (cancellationReason: FetchFailureReason) =>
        logFetchCancellation({
            logEvent,
            error,
            failureReason: cancellationReason,
            input: url,
            userAgent: navigator.userAgent,
            userId,
        });

    if (isBenignFetchFailure(error, failureReason)) {
        onSettled?.(failureReason);
        logCancellation(failureReason);
        return;
    }

    settleFetchFailureReason(failureReason, { keepalive, wasRefused }, (settledReason) => {
        onSettled?.(settledReason);
        if (isBenignFetchFailure(error, settledReason)) {
            logCancellation(settledReason);
            return;
        }

        logEvent({
            message,
            tag: EVENT_SEND_LOG_EVENT.TAG,
            data: {
                ...data,
                failure_reason: settledReason,
                error_message: describeError(error).errorMessage,
            },
        });
    });
}

/**
 * Logs a guest's failed send: like `fetchBase`, the send resolves to a
 * transport failure instead of rejecting.
 */
function logGuestSendFailure(
    logEvent: LogEvent,
    error: unknown,
    { url, eventType, eventName, keepalive, wasRefused, onSettled, userId }: GuestSendContext
): void {
    logSendFailure(logEvent, error, {
        url,
        userId,
        message: EVENT_SEND_LOG_EVENT.GUEST_SEND_FAILED,
        data: { event_type: eventType, event_name: eventName, keepalive: keepalive && !wasRefused },
        keepalive,
        wasRefused,
        onSettled,
    });
}

/**
 * Logs a batch the API did not take: a status it answered (and whether the
 * events were then sent one by one) or the error the request failed with.
 */
function logBatchSendFailure(
    logEvent: LogEvent,
    failure: WebEventBatchFailure,
    userId: string | null | undefined
): void {
    const data = { events: failure.events, keepalive: failure.keepalive };

    if ("error" in failure) {
        logSendFailure(logEvent, failure.error, {
            url: failure.url,
            userId,
            message: EVENT_SEND_LOG_EVENT.BATCH_SEND_FAILED,
            data,
            keepalive: failure.keepaliveRequested,
            wasRefused: failure.keepaliveRequested && !failure.keepalive,
        });
        return;
    }

    logEvent({
        message: EVENT_SEND_LOG_EVENT.BATCH_SEND_FAILED,
        tag: EVENT_SEND_LOG_EVENT.TAG,
        data: {
            ...data,
            status: failure.status,
            sent_alone: failure.sentAlone,
            interstitial: failure.interstitial,
        },
    });
}

export function useUnauthorizedEventService() {
    const fetchBase = useFetch();
    const { tokensInfoRef } = useAuthTokens();
    const isAuth = tokensInfoRef.current?.token;
    const { userId } = useUserId();
    const logEvent = useLogEvent();

    return useCallback(
        (data: EventRequest, requestConfig?: RequestConfigType) => {
            if (data.type === BiqQueryPropertyType.WEB_EVENT && !data.properties.user_id) {
                data.properties.user_id = userId;
            }

            if (data.type === FacebookPropertyType.FACEBOOK_EVENT) {
                if (!data.properties.user_data) {
                    data.properties.user_data = {};
                }
                if (!data.properties.user_data.external_id) {
                    data.properties.user_data.external_id = sha256(userId || "");
                }
            }

            const eventName =
                data.type === BiqQueryPropertyType.WEB_EVENT
                    ? data.properties.event_name
                    : undefined;
            const url = `${EVENTS_URL}${SEND_UNAUTHORIZED_EVENT_PATH}`;
            const send = () => {
                const body = JSON.stringify(data);
                // Keepalive lets the request finish after the page is closed or
                // navigated away, but only while it fits the budget: past the
                // browser's quota it is refused outright.
                const releaseKeepalive = reserveKeepalive(body);
                const isKeepalive = releaseKeepalive !== null;
                const init: RequestInit = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-lang": "en",
                    },
                    body,
                    ...requestConfig,
                    keepalive: isKeepalive,
                };

                const skipErrorBeacon = eventName === ERROR_BEACON_EVENT_NAME;
                // A refused keepalive request goes again without and gives its
                // room in the budget back at once; `fetchBase` does the same
                // for a signed-in user.
                const sendAsGuest = () => {
                    const refusal = trackKeepaliveRefusal({
                        sender: KEEPALIVE_SENDER.GUEST_EVENT,
                        release: releaseKeepalive ?? undefined,
                        logEvent,
                    });
                    return fetchWithKeepaliveFallback((attemptInit) => fetch(url, attemptInit), init, refusal.onRefused)
                        .then((sent) => {
                            refusal.reportDelivered(sent.status);
                            return wrapperFetchJsonResponse<EventResponse>(sent);
                        })
                        .catch((error: unknown) => {
                            logGuestSendFailure(logEvent, error, {
                                url,
                                eventType: data.type,
                                eventName,
                                keepalive: isKeepalive,
                                wasRefused: refusal.wasRefused(),
                                onSettled: refusal.reportFailed,
                                userId,
                            });
                            return wrapperFetchJsonResponse<EventResponse>(undefined);
                        });
                };
                const response = isAuth
                    ? fetchBase(url, init, {
                        skipErrorBeacon,
                        skipTimeout: true,
                        onKeepaliveRefused: releaseKeepalive ?? undefined,
                    }).then(wrapperFetchJsonResponse<EventResponse>)
                    : sendAsGuest();

                return response.finally(() => releaseKeepalive?.());
            };

            // Click events wait for a batch, which goes out as one
            // request with no CORS preflight (`web-event-batch.ts`). A caller that
            // passes its own request config gets a request of its own.
            const isQueued =
                !requestConfig &&
                isBatchedWebEventName(eventName) &&
                queueWebEvent(data, {
                    sendAlone: () => {
                        const logFailure = (error: unknown) =>
                            logSendFailure(logEvent, error, {
                                url,
                                userId,
                                message: EVENT_SEND_LOG_EVENT.BATCHED_SEND_FAILED,
                                data: { event_name: eventName },
                                keepalive: false,
                                wasRefused: false,
                            });
                        // The queue resends a whole batch in a loop, so a throw here
                        // would stop the events after this one; an async function
                        // turns it into a rejection.
                        const trySend = async () => send();
                        trySend().catch(logFailure);
                    },
                    onBatchFailed: (failure) => logBatchSendFailure(logEvent, failure, userId),
                });

            return isQueued ? Promise.resolve(queuedEventResponse()) : send();
        },
        [fetchBase, isAuth, logEvent, userId]
    );
}
